import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../pages/AuthContext';
import { motion } from 'framer-motion';
import API_BASE_URL from '../config/api';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-users-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailID: email, passwordhash: password }),
      });
      const data = await res.json();
      if (res.ok) { login(data.token, data.email, data.role, data.name, data.surname); navigate('/'); }
      else setError(data.message || 'Login failed. Please check your credentials.');
    } catch { setError('Unable to connect to server. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ background: '#F8F5EE' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0F172A 0%, #16213E 100%)', borderRight: '1px solid rgba(212,175,55,0.12)' }}>
        {/* BG watermark */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none" style={{ fontSize: '60vw', fontFamily: '"Cormorant Garamond", serif', color: 'white', overflow: 'hidden' }}>ॐ</div>
        <div className="relative z-10">
          <h2 className="text-5xl font-bold leading-tight mb-6 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Preserving<br />
            <span style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Sacred</span><br />
            Traditions
          </h2>
          <div className="vrv-divider mb-6" style={{ width: '60px' }} />
          <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif' }}>
            Join a worldwide community dedicated to spirituality, unity, and the ancient Vaikhanasa heritage.
          </p>
        </div>
        <p className="text-xs italic relative z-10" style={{ color: 'rgba(212,175,55,0.3)', fontFamily: '"Cormorant Garamond", serif' }}>
          "In service of Lord Vishnu and the Vaikhanasa tradition"
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 lg:px-16 py-8 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <p className="vrv-tag mb-1" style={{ color: '#D4AF37' }}>Welcome Back</p>
          <h1 className="text-4xl font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#111827' }}>Sign In</h1>
          <p className="text-sm mb-6" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif' }}>Access your VRV account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="vrv-label">Email Address</label>
              <input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                className="vrv-input" required autoComplete="email"
                readOnly
                onFocus={e => e.target.removeAttribute('readOnly')} />
            </div>
            <div>
              <label className="vrv-label">Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
                className="vrv-input" required autoComplete="current-password" />
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs font-semibold transition-colors" style={{ color: '#16213E' }}
                onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
                onMouseLeave={e => e.currentTarget.style.color = '#16213E'}>
                Forgot Password?
              </Link>
            </div>

            <button type="submit" disabled={loading} className="btn-gold w-full" style={{ height: '48px', fontSize: '0.85rem' }}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing In…
                </span>
              ) : 'Sign In →'}
            </button>
          </form>

          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-3 px-4 py-2.5 rounded-xl text-sm flex items-start gap-2"
              style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontFamily: 'Inter, sans-serif' }}>
              <span>⚠️</span><span>{error}</span>
            </motion.div>
          )}

          <p className="text-sm mt-5 text-center" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif' }}>
            Don't have an account?{' '}
            <Link to="/signup" className="font-bold transition-colors" style={{ color: '#16213E' }}
              onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
              onMouseLeave={e => e.currentTarget.style.color = '#16213E'}>
              Create Account →
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
